import Fuse from 'fuse.js';

// Generic type for searchable items
export interface SearchableItem {
  [key: string]: any;
}

// Configuration interface for search options
export interface SearchConfig<T> {
  keys: Array<keyof T | string>;
  threshold?: number;
  distance?: number;
  includeScore?: boolean;
  includeMatches?: boolean;
  minMatchCharLength?: number;
  shouldSort?: boolean;
}

// Default search configuration
const defaultConfig: Partial<SearchConfig<any>> = {
  threshold: 0.3, // 0.0 = perfect match, 1.0 = match anything
  distance: 100,
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2,
  shouldSort: true,
};

/**
 * Creates a fuzzy search instance with Fuse.js
 * @param items - Array of items to search through
 * @param config - Search configuration options
 * @returns Fuse instance ready for searching
 */
export function createFuzzySearch<T extends SearchableItem>(
  items: T[],
  config: SearchConfig<T>
): Fuse<T> {
  const options = { ...defaultConfig, ...config };
  return new Fuse(items, options);
}

/**
 * Performs a fuzzy search and returns formatted results
 * @param fuse - Fuse instance
 * @param query - Search query string
 * @returns Array of search results with scores and matches
 */
export function performSearch<T>(
  fuse: Fuse<T>,
  query: string
): Array<{
  item: T;
  score?: number;
  matches?: readonly Fuse.FuseResultMatch[];
}> {
  if (!query.trim()) {
    return [];
  }

  return fuse.search(query).map(result => ({
    item: result.item,
    score: result.score,
    matches: result.matches,
  }));
}

/**
 * Utility function to highlight matched text in search results
 * @param text - Original text
 * @param matches - Array of match indices from Fuse.js
 * @returns Text with highlighted matches (you can customize the highlighting)
 */
export function highlightMatches(
  text: string,
  matches?: readonly Fuse.FuseResultMatch[]
): string {
  if (!matches || matches.length === 0) {
    return text;
  }

  // This is a basic implementation - you can customize the highlighting
  // For now, we'll just return the original text
  // In a real implementation, you might wrap matches in <mark> tags or similar
  return text;
}

export default createFuzzySearch;
